import React, { FC, useState, useMemo } from "react";
import { Fade } from "react-awesome-reveal";
import classNames from "classnames";
import { Checkmark } from "@app/components/common/icons";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import { LinkButtonAdapter } from "../link-button-adapter/LinkButtonAdapter";
import { Mandate, RichText } from "../../models";
import { BlockHeader } from "../../../common/block-header/BlockHeader";
import { BlockBody } from "../../../common/block-body/BlockBody";
import { Toggle } from "./components/toggle/Toggle";

type Props = {
  title: RichText;
  guidedBy: Mandate[];
  managedBy: Mandate[];
  isColorBackground?: boolean;
};

export const Mandates: FC<Props> = ({
  title,
  guidedBy,
  managedBy,
  isColorBackground,
}) => {
  const [active, setActive] = useState(
    managedBy[0]?.title ?? guidedBy[0]?.title,
  );
  const toggleOptions = useMemo(
    () => [
      ...managedBy.map((mandate) => mandate.title),
      ...guidedBy.map((mandate) => mandate.title),
    ],
    [guidedBy, managedBy],
  );
  const selected = useMemo(
    () =>
      [...managedBy, ...guidedBy].filter(
        (mandate) => mandate.title === active,
      )[0],
    [guidedBy, managedBy, active],
  );
  const header = useMemo(() => <BlockHeader richText={title} />, [title]);

  return (
    <div
      className={classNames(
        "mt-20 px-5",
        "lg:mt-25 lg:px-16",
        "xl:mt-40 xl:px-24",
        { "bg-dark py-20": isColorBackground },
        { "lg:py-25": isColorBackground },
        { "xl:py-30": isColorBackground },
      )}
    >
      <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
        <div
          className={classNames(
            "text-primary-content mx-auto flex w-full flex-col items-center gap-20",
            "md:gap-15",
            "xl:max-w-container xl:gap-30",
          )}
        >
          <div className="flex flex-col items-center">
            <div
              className={classNames(
                "text-large mb-10 text-center",
                "xl:text-xx-large",
              )}
            >
              {header}
            </div>
            <Toggle
              active={active}
              options={toggleOptions}
              onUpdateActive={(val) => setActive(val)}
            />
          </div>

          <div
            className={classNames(
              "flex w-full flex-col gap-10",
              "md:flex-row",
              "lg:gap-15",
              "xxl:justify-between xxl:gap-0 xxl:px-24",
            )}
          >
            <div
              className={classNames(
                "text-medium",
                "md:text-medium md:basis-1/2",
                "xl:text-large",
                "xxl:w-[43%] xxl:basis-auto",
              )}
            >
              <BlockBody richText={selected.description} />
            </div>
            <ul
              className={classNames(
                "text-regular space-y-5",
                "md:basis-1/2",
                "lg:pl-10",
                "xxl:w-[42%] xxl:basis-auto",
              )}
            >
              {selected.benefits.map((benefit) => (
                <li key={benefit._uid} className="flex gap-5">
                  <div>
                    <Checkmark className="h-10 w-10 -translate-y-1.5" />
                  </div>
                  <BlockBody richText={benefit.title} />
                </li>
              ))}
            </ul>
          </div>
          {selected.button?.length ? (
            <LinkButtonAdapter data={selected.button[0]} />
          ) : null}
        </div>
      </Fade>
    </div>
  );
};
